// Setup, behind one door.
//
// Six screens used to sit in the left rail beside daily work: Firm
// Settings, Users, Task Templates, Clio Data Sync, Webhooks, Template
// Verifier. They are configuration — touched when something changes,
// which for most of them is monthly at best. Six permanent rows of
// noise for that, in a rail a paralegal reads twenty times a day.
//
// They are all still one click from the rail and two from anywhere.
// What changes is that they no longer compete with Matters and
// Deadlines for the same glance.
//
// The individual `page === "..."` routes are deliberately kept in
// app.jsx. Nothing outside the nav navigates to most of them, but
// audit-log is reached from the matter Activity card and the Firm
// Overview, and a route that exists costs nothing next to a dead link
// somebody finds later.

import React, { useState } from "react";
import { C, roleAtLeast } from "../constants.js";
import { I } from "../icons.jsx";

import { FirmSettingsPage } from "./FirmSettingsPage.jsx";
import { UsersAdminPage } from "./UsersAdminPage.jsx";
import { TaskTemplatesPage } from "./TaskTemplatesPage.jsx";
import { DataSyncPage } from "./DataSyncPage.jsx";
import { WebhooksAdminPage } from "./WebhooksAdminPage.jsx";
import { TemplateSweepPage } from "./TemplateSweepPage.jsx";
import { AuditLogPage } from "./AuditLogPage.jsx";

// `blurb` is the point of this screen. A list of six admin nouns tells
// somebody nothing about which one they want; one line each does.
const SECTIONS = [
  { id: "firm-settings", label: "Firm Settings", minRole: "superadmin",
    blurb: "Fee terms, letterhead, practice thresholds, statute periods." },
  { id: "users", label: "Users", minRole: "superadmin",
    blurb: "Who can sign in, and what each person is allowed to see." },
  { id: "task-templates", label: "Task Templates", minRole: "superadmin",
    blurb: "The firm's process — the steps a matter picks up at each stage." },
  { id: "data-sync", label: "Clio Data Sync", minRole: "superadmin",
    blurb: "Pull matters and contacts across from Clio." },
  { id: "webhooks", label: "Webhooks", minRole: "superadmin",
    blurb: "What Clio notifies this app about as it happens." },
  { id: "template-verifier", label: "Template Verifier", minRole: "superadmin",
    blurb: "Check every document template's merge fields still resolve." },
  { id: "audit-log", label: "Audit Log", minRole: "partner",
    blurb: "Who did what, including every read of a client's medical file." },
];

export function SettingsHubPage({
  authStatus, currentFirm, onFirmChange, showNotification, onMatterClick,
  initialSection = null,
}) {
  const role = authStatus?.firm_user?.role || "pending";
  const allowed = SECTIONS.filter(s => roleAtLeast(role, s.minRole));
  const [section, setSection] = useState(initialSection);

  const body = (id) => {
    switch (id) {
      case "firm-settings":
        return React.createElement(FirmSettingsPage, {
          authStatus, currentFirm, onFirmChange, showNotification });
      case "users":
        return React.createElement(UsersAdminPage, { showNotification, authStatus });
      case "task-templates":
        return React.createElement(TaskTemplatesPage, { showNotification });
      case "data-sync":
        return React.createElement(DataSyncPage, null);
      case "webhooks":
        return React.createElement(WebhooksAdminPage, { showNotification });
      case "template-verifier":
        return React.createElement(TemplateSweepPage, { showNotification });
      case "audit-log":
        return React.createElement(AuditLogPage, { onMatterClick });
      default:
        return null;
    }
  };

  if (section) {
    const current = allowed.find(s => s.id === section);
    if (!current) {
      // The role changed, or a stale link. Back to the index rather
      // than a blank pane.
      setSection(null);
      return null;
    }
    return React.createElement("div", null,
      React.createElement("div", {
        style: { padding: "20px 40px 0", maxWidth: "1280px" },
      },
        React.createElement("button", {
          onClick: () => setSection(null),
          style: {
            display: "flex", alignItems: "center", gap: "6px",
            border: "none", background: "none", padding: 0, cursor: "pointer",
            color: C.textMuted, fontSize: "13px",
          },
        }, I.back, "All settings"),
      ),
      body(section),
    );
  }

  if (allowed.length === 0) {
    return React.createElement("div", { style: { padding: "32px 40px" } },
      React.createElement("p", { style: { fontSize: "14px", color: C.textMuted } },
        "Nothing here is available to your role."));
  }

  return React.createElement("div",
    { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1000px" } },
    React.createElement("h1", {
      style: {
        fontFamily: "'Playfair Display',serif", fontSize: "28px",
        fontWeight: 600, color: C.navy, marginBottom: "6px",
      },
    }, "Settings"),
    React.createElement("p", {
      style: { fontSize: "15px", color: C.textMuted, marginBottom: "24px" },
    }, "Configuration and firm records. Nothing here is part of daily case work."),

    React.createElement("div", {
      style: {
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        gap: "12px",
      },
    },
      allowed.map(s => React.createElement("button", {
        key: s.id,
        onClick: () => setSection(s.id),
        style: {
          textAlign: "left", cursor: "pointer", background: "#fff",
          border: `1px solid ${C.warmGray}`, borderRadius: "8px",
          padding: "16px 18px",
        },
      },
        React.createElement("div", {
          style: { fontSize: "14px", fontWeight: 600, color: C.navy, marginBottom: "4px" },
        }, s.label),
        React.createElement("div", {
          style: { fontSize: "12px", color: C.textMuted, lineHeight: 1.5 },
        }, s.blurb),
      ))),
  );
}
